<template>
  <div>
    <div class="project__filters">
      <CustomSelect
        :options="residentials"
        label="ЖК"
        placeholder="Выберите ЖК"
        optionValueKey="resident_id"
        optionLabelKey="resident_name"
        :value="filters.resident_id"
        @change="(value) => onFiltersChange('resident_id', value)"
        class="project__filter"
      />
      <CustomSelect
        :options="projectTypes"
        label="Тип проекта"
        optionValueKey="project_type_id"
        optionLabelKey="project_type_name"
        :value="filters.project_type_id"
        class="project__filter"
        @change="(value) => onFiltersChange('project_type_id', value)"
      />
    </div>

    <CustomTable
      title="Сводная по проектам"
      :rowsData="data"
      :columns="columns"
      :stickyHeader="false"
      :loading="isFetching"
      :custom-body="true"
      :small="true"
    >
      <template #top-right>
        <slot name="top-right">
          <q-btn
            :loading="isFetching"
            @click="refetch"
            color="primary"
            size="sm"
          >
            <q-icon name="replay" />
          </q-btn>
        </slot>
      </template>

      <template #customBody="props">
        <q-tr>
          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.resident_name }}
          </custom-table-cell>

          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.project_type_name }}
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            {{ props.row.entrances?.join(', ') }}
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            {{ props.row.flats }}
          </custom-table-cell>

          <custom-table-cell align="right" vertical-align="center">
            <Amount :value="props.row.total_sum" suffix=" ₸" />
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            <div
              v-if="getStatusInfo(props.row.status)"
              :style="{
                backgroundColor: getStatusInfo(props.row.status).color,                
              }"
              class="status-badge"
              :title="getStatusInfo(props.row.status).description"
            >
              {{ getStatusInfo(props.row.status).description || 'Статус' }}
            </div>
            <span v-else>{{ props.row.status }}</span>
          </custom-table-cell>

          <custom-table-cell align="right" vertical-align="center">
            <Amount :value="props.row.payment_sum" suffix=" ₸" />
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            <q-chip
              :color="getPercentageColor(props.row.percentage_of_payment)"
              text-color="white"
              :label="`${props.row.percentage_of_payment}%`"
              size="sm"
            />
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            <q-chip
              :color="getPercentageColor(props.row.floor_percentage)"
              text-color="white"
              :label="`${props.row.floor_percentage}%`"
              size="sm"
            />
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomSelect from "@/components/form/CustomSelect";
import Amount from "@/components/common/Amount";

import {
  getProjectsCommonInfo,
  getResidentialsList,
  getProjectTypes,
  projectsOverviewColumns,
} from "@/views/ProjectsOverview/services";

import { useQuery } from "@tanstack/vue-query";

const columns = projectsOverviewColumns;
const filters = ref({
  resident_id: "",
  project_type_id: "",
});

const { data, isFetching, refetch } = useQuery({
  queryKey: computed(() => ["projectsCommonInfo", filters.value]),
  queryFn: () => getProjectsCommonInfo(filters.value),
  initialData: []
});

const { data: residentials } = useQuery({
  queryKey: ["residentials"],
  queryFn: () => getResidentialsList()
});

const { data: projectTypes } = useQuery({
  queryKey: computed(() => ["projectTypes", filters.value.resident_id]),
  queryFn: () => getProjectTypes(filters.value.resident_id),
  enabled: computed(() => !!filters.value.resident_id)
});

const onFiltersChange = (key, value) => {
  if (key === 'resident_id') {
    filters.value = {
      ...filters.value,
      [key]: value,
      project_type_id: ''
    };
  } else {
    filters.value[key] = value;
  }
};

const getStatusInfo = (status) => {
  try {
    if (typeof status === 'string' && status.startsWith('{')) {
      return JSON.parse(status);
    }
    if (typeof status === 'object' && status !== null) {
      return status;
    }
    return null;
  } catch (e) {
    return null;
  }
};

const getPercentageColor = (percentage) => {
  if (percentage === 0) {
    return 'red';
  } else if (percentage === 100) {
    return 'green';
  } else {
    return 'orange';
  }
};
</script>

<style scoped lang="scss">
.project__filters {
  margin-bottom: 25px;
  display: flex;
  gap: 20px;
  align-items: flex-end;
  .project__filter {
    width: 190px;
  }
}
.status-badge {
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  display: inline-block;
}
</style>
